import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Search, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FilterCombobox } from "@/components/FilterCombobox"
import type { InventoryFilters as InventoryFiltersType, SelectOption } from "@/types/inventory"
import type { AssetType, AssetTypeInfo } from "@/lib/assetTypes"

interface InventoryFiltersProps {
  filters: InventoryFiltersType
  assetType: AssetType
  assetTypes: AssetTypeInfo[]
  stages: SelectOption[]
  customers: SelectOption[]
  locations: SelectOption[]
  tags: SelectOption[]
  products?: SelectOption[]
  containers?: SelectOption[]
  volumes?: SelectOption[]
  onSearch: (filters: Partial<InventoryFiltersType>) => void
  onAssetTypeChange: (type: AssetType) => void
}

export function InventoryFilters({
  filters, assetType, assetTypes, stages, customers, locations, tags, products, containers, volumes, onSearch, onAssetTypeChange,
}: InventoryFiltersProps) {
  const { t } = useTranslation()
  const isKeg = assetType === "Keg"
  const [localFilters, setLocalFilters] = useState<Partial<InventoryFiltersType>>({
    code: filters.code,
    product_id: filters.product_id,
    container_id: filters.container_id,
    volume: filters.volume,
    stage_id: filters.stage_id,
    customer_id: filters.customer_id,
    location_id: filters.location_id,
    tag_id: filters.tag_id,
  })

  function handleChange(key: keyof InventoryFiltersType, value: string) {
    setLocalFilters((prev) => ({ ...prev, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSearch(localFilters)
  }

  function handleClear() {
    const emptyFilters: Partial<InventoryFiltersType> = {
      code: "", stage_id: "", customer_id: "", location_id: "", tag_id: "",
    }
    if (isKeg) { emptyFilters.product_id = ""; emptyFilters.container_id = ""; emptyFilters.volume = "" }
    setLocalFilters(emptyFilters)
    onSearch(emptyFilters)
  }

  const gridClass = isKeg
    ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-10 gap-4"
    : "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4"

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={gridClass}>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">{t("inventory.filters.assetType")}</label>
          <Select value={assetType} onValueChange={(value) => onAssetTypeChange(value as AssetType)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {assetTypes.map(({ type, key }) => (
                <SelectItem key={type} value={type}>
                  {t(`assetTypes.${key}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">{t("inventory.filters.code")}</label>
          <Input value={localFilters.code || ""} onChange={(e) => handleChange("code", e.target.value)} placeholder={t("inventory.filters.codePlaceholder")} />
        </div>
        {isKeg && products && (
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">{t("inventory.filters.product")}</label>
            <FilterCombobox value={localFilters.product_id || ""} onChange={(value) => handleChange("product_id", value)} options={products} placeholder={t("inventory.filters.all")} className="w-full" />
          </div>
        )}
        {isKeg && containers && (
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">{t("inventory.filters.container")}</label>
            <FilterCombobox value={localFilters.container_id || ""} onChange={(value) => handleChange("container_id", value)} options={containers} placeholder={t("inventory.filters.all")} className="w-full" />
          </div>
        )}
        {isKeg && volumes && (
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">{t("inventory.filters.volume")}</label>
            <FilterCombobox value={localFilters.volume || ""} onChange={(value) => handleChange("volume", value)} options={volumes} placeholder={t("inventory.filters.all")} className="w-full" />
          </div>
        )}
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">{t("inventory.filters.stage")}</label>
          <FilterCombobox value={localFilters.stage_id || ""} onChange={(value) => handleChange("stage_id", value)} options={stages} placeholder={t("inventory.filters.all")} showColorDot className="w-full" />
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">{t("inventory.filters.customer")}</label>
          <FilterCombobox value={localFilters.customer_id || ""} onChange={(value) => handleChange("customer_id", value)} options={customers} placeholder={t("inventory.filters.all")} className="w-full" />
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">{t("inventory.filters.location")}</label>
          <FilterCombobox value={localFilters.location_id || ""} onChange={(value) => handleChange("location_id", value)} options={locations} placeholder={t("inventory.filters.all")} className="w-full" />
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">{t("inventory.filters.tag")}</label>
          <FilterCombobox value={localFilters.tag_id || ""} onChange={(value) => handleChange("tag_id", value)} options={tags} placeholder={t("inventory.filters.all")} showColorDot className="w-full" />
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">&nbsp;</label>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1"><Search className="w-4 h-4 mr-2" />{t("inventory.filters.search")}</Button>
            <Button type="button" variant="outline" onClick={handleClear} title={t("inventory.filters.clear")}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </form>
  )
}
